import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { logger } from "@/lib/logger";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subjectOptions = [
  { value: "telephone", label: "Réparation téléphone" },
  { value: "ordinateur", label: "Réparation ordinateur" },
  { value: "professionnel", label: "Services professionnels" },
  { value: "autre", label: "Autre demande" }
];

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    device_type: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, device_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/contact`, formData);
      setIsSuccess(true);
      toast.success("Message envoyé !", {
        description: "Nous vous répondrons dans les plus brefs délais."
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        device_type: "",
        message: ""
      });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      logger.error("Error submitting contact:", error);
      toast.error("Erreur lors de l'envoi", {
        description: "Veuillez réessayer ou nous appeler directement."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full" data-testid="contact-form">
      <CardHeader>
        <CardTitle>Nous contacter</CardTitle>
        <CardDescription>
          Une question ? Un renseignement ? Écrivez-nous !
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Jean Dupont"
                required
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="jean@email.com"
                required
                data-testid="contact-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="06 12 34 56 78"
                required
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="device_type">Sujet</Label>
              <Select value={formData.device_type} onValueChange={handleSelectChange}>
                <SelectTrigger data-testid="contact-subject-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {subjectOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Décrivez votre demande..."
              rows={5}
              required
              data-testid="contact-message-textarea"
            />
          </div>

          <Button 
            type="submit" 
            size="lg"
            className="w-full bg-blue-600 hover:bg-blue-700 h-12"
            disabled={isSubmitting}
            data-testid="contact-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Envoi en cours...
              </>
            ) : isSuccess ? (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Message envoyé !
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Envoyer le message
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
